import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CubeIcon, 
  WifiIcon, 
  CloudIcon,
  DevicePhoneMobileIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { usePWA } from '../contexts/PWAContext';
import { 
  H1, 
  H2, 
  H3, 
  Body, 
  Card, 
  Button, 
  Badge 
} from '../components/ui';
import { 
  PWAStatus, 
  InstallPrompt, 
  OfflineIndicator, 
  UpdatePrompt 
} from '../components/pwa';

const PWADemo: React.FC = () => {
  const { 
    isSupported, 
    isInstalled, 
    isInstallable, 
    isOffline, 
    hasUpdate: _hasUpdate,
    isLoading: _isLoading,
    installApp: _installApp,
    updateApp: _updateApp,
    cacheImportantData,
    clearCache
  } = usePWA();

  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [promptVariant, setPromptVariant] = useState<'card' | 'banner' | 'modal'>('modal');

  const features = [
    {
      icon: DevicePhoneMobileIcon,
      title: 'Offline Access',
      description: 'Access your observations and data even without internet connection',
      available: isSupported,
    },
    {
      icon: ArrowPathIcon,
      title: 'Automatic Updates',
      description: 'Get the latest features and fixes automatically',
      available: isSupported,
    },
    {
      icon: CloudIcon,
      title: 'Background Sync',
      description: 'Your changes sync automatically when you come back online',
      available: isSupported,
    },
    {
      icon: WifiIcon,
      title: 'Fast Loading',
      description: 'Cached resources mean faster loading times',
      available: isSupported,
    },
  ];

  const demoActions = [
    {
      title: 'Install App',
      description: 'See the install prompt in action',
      action: () => setShowInstallPrompt(true),
      available: isInstallable,
      variant: 'primary' as const,
    },
    {
      title: 'Update Prompt',
      description: 'Demo the update notification',
      action: () => setShowUpdatePrompt(true),
      available: true,
      variant: 'secondary' as const,
    },
    {
      title: 'Cache Data',
      description: 'Pre-cache important data for offline use',
      action: cacheImportantData,
      available: isSupported,
      variant: 'outline' as const,
    },
    {
      title: 'Clear Cache',
      description: 'Remove all cached data',
      action: clearCache,
      available: isSupported,
      variant: 'ghost' as const,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center space-y-4"
      >
        <H1 className="flex items-center justify-center space-x-3">
          <CubeIcon className="w-8 h-8 text-primary-600" />
          <span>Progressive Web App</span>
        </H1>
        <Body className="text-xl text-gray-600 dark:text-gray-400">
          Sprout is a Progressive Web App with offline capabilities and native app features
        </Body>
      </motion.div>

      {/* Current Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <PWAStatus detailed />
        
        <Card className="p-6">
          <H3 className="mb-4">Live Status</H3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Connection Status</span>
              <div className="flex items-center space-x-2">
                <OfflineIndicator variant="badge" />
                {!isOffline && (
                  <Badge variant="success">Online</Badge>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Install Status</span>
              <Badge variant={isInstalled ? 'success' : 'default'}>
                {isInstalled ? 'Installed' : 'Web Version'}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Service Worker</span>
              <Badge variant={isSupported ? 'success' : 'error'}>
                {isSupported ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Features */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <H2 className="mb-6">PWA Features</H2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="p-6 h-full">
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 rounded-lg ${
                    feature.available 
                      ? 'bg-green-100 dark:bg-green-900' 
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <feature.icon className={`w-6 h-6 ${
                      feature.available ? 'text-green-600' : 'text-gray-400'
                    }`} />
                  </div>
                  {feature.available ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                  )}
                </div>
                <H3 className="mb-2">{feature.title}</H3>
                <Body className="text-sm text-gray-600 dark:text-gray-400">
                  {feature.description}
                </Body>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Demo Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <H2 className="mb-6">Demo Actions</H2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {demoActions.map((action) => (
            <Card key={action.title} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  <H3 className="mb-2">{action.title}</H3>
                  <Body className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {action.description}
                  </Body>
                  <Button
                    variant={action.variant}
                    onClick={action.action}
                    disabled={!action.available}
                    className="w-full"
                  >
                    {action.title}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </motion.div>

      {/* Prompt Variants */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <Card className="p-6">
          <H3 className="mb-4">Prompt Variants</H3>
          <div className="space-y-4">
            <div>
              <Body className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Choose a variant for the demo prompts:
              </Body>
              <div className="flex space-x-2">
                {(['card', 'banner', 'modal'] as const).map((variant) => (
                  <Button
                    key={variant}
                    variant={promptVariant === variant ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setPromptVariant(variant)}
                  >
                    {variant.charAt(0).toUpperCase() + variant.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            
            {promptVariant === 'card' && (
              <div className="space-y-4">
                <H3 className="text-sm">Card Variants Preview</H3>
                <InstallPrompt variant="card" />
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >
        <Card className="p-6">
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="w-6 h-6 text-blue-500 mt-1" />
            <div>
              <H3 className="mb-2">About PWA Implementation</H3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  This PWA implementation includes:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Service Worker for offline functionality</li>
                  <li>Cache-first strategy for static assets</li>
                  <li>Background sync for data updates</li>
                  <li>Install prompts for mobile and desktop</li>
                  <li>Automatic updates with user notification</li>
                  <li>Offline fallbacks for API requests</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Demo Modals */}
      {showInstallPrompt && (
        <InstallPrompt
          variant={promptVariant}
          onClose={() => setShowInstallPrompt(false)}
        />
      )}
      
      {showUpdatePrompt && (
        <UpdatePrompt
          variant={promptVariant}
          onClose={() => setShowUpdatePrompt(false)}
        />
      )}
    </div>
  );
};

export default PWADemo;
